import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { RootState } from '@/redux/store';
import { getFilterUrl, showToastNotification } from '@/utility/helper';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { IOfficeResponse } from '../OfficeLocation/OfficeLocation';
import {
  IAddSiteSalaryHeadForm,
  ISalaryHead,
  ISalaryHeadConfig,
  ISalaryHeadDetailResponse,
} from '@/interfaces/salary.model';
import Layout from '@/components/Layout/Layout';
import BackButton from '@/components/ui/BackButton';
import EditIcon from '@/components/Icons/EditIcon';
import DeleteUnfilledIcon from '@/components/Icons/DeleteUnfilledIcon';
import FormButtons from '@/components/common/Form/FormButtons';
import { ISalaryApiResponse } from '../SalaryHead.tsx/SalaryHead';
import AddEarningModal from '@/components/Modal/AddEarningModal';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import AddDeductionModal from '@/components/Modal/AddDeductionModal';
import CardLayout from '@/components/Layout/CardLayout';
import { IDeleteResponse } from '@/interfaces/common.model';
import Button from '@/components/ui/Button';

export interface ISiteSalaryHeads {
  earning: ISalaryHeadConfig[];
  deduction: ISalaryHeadConfig[];
}

export interface ICustomerData {
  office: string;
  id: string;
}

interface IPageStates {
  siteList: [];
  earnings: ISalaryHead[];
  deductions: ISalaryHead[];
  isLoading: boolean;
  CustomerData?: ICustomerData[];
  count?: number;
  totalPages?: number;
}

type TSalaryType = keyof ISiteSalaryHeads;

const AddSiteSalaryHead = () => {
  const location = useLocation();
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>('');
  const [selectedType, setSelectedType] = useState<TSalaryType | null>(null);

  const [selectedSalaryHeads, setSelectedSalaryHeads] =
    useState<ISiteSalaryHeads>({
      earning: [],
      deduction: [],
    });
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const [modalData, setModalData] = useState<ISalaryHeadConfig | null>(null);
  const [modal, setModal] = useState(false);
  const toggleModal = () => {
    setModal(!modal);
  };

  const [earningModal, setEarningModal] = useState(false);
  const toggleEarningModal = () => {
    setEarningModal(!earningModal);
  };
  const [deductionModal, setDeductionModal] = useState(false);
  const toggleDeductionModal = () => {
    setDeductionModal(!deductionModal);
  };
  const { handleSubmit, reset, watch } = useForm<IAddSiteSalaryHeadForm>();
  const selectedId = watch('officeId');
  const [pageStates, setPageStates] = useState<IPageStates>({
    siteList: [],
    earnings: [],
    deductions: [],
    isLoading: false,
  });

  const getOfficeList = useCallback(
    async (companyId = null) => {
      try {
        setPageStates((prev) => ({
          ...prev,
          isLoading: true,
        }));
        const filterState = {
          companyId: companyId,
          type: 'site',
          pageSize: 100,
          orderBy: filterDetails?.orderBy,
        };
        const API = `${API_URL}${API_ENDPOINT?.getSite}?${getFilterUrl(filterState)}`;

        const response: IOfficeResponse = await callApi(API, {
          method: 'GET',
        });
        const newData = response?.data?.site?.map((item) => ({
          office: item?.name || '',
          id: item?.id,
        }));
        setPageStates((prevState) => ({
          ...prevState,
          CustomerData: newData,
          count: response?.data?.pageInfo?.total ?? 0,
          totalPages: response?.data?.pageInfo?.totalPages ?? 0,
        }));
      } catch (error) {
        if (error && typeof error === 'object' && 'message' in error) {
          showToastNotification('danger', (error as IApiErrorResponse).message);
        } else {
          showToastNotification('danger', 'Something went wrong');
        }
      } finally {
        setPageStates((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }
    },
    [filterDetails?.orderBy, setPageStates],
  );

  const getSalaryHead = useCallback(async (type: string) => {
    try {
      const response: ISalaryApiResponse = await callApi(
        API_URL + API_ENDPOINT.getSalaryHead + `pageSize=50&type=${type}`,
        {
          method: 'GET',
        },
      );
      if (response?.status) {
        setPageStates((pre) => ({
          ...pre,
          ...(type === 'earning' && { earnings: response?.data?.salaryHead }),
          ...(type === 'deduction' && {
            deductions: response?.data?.salaryHead,
          }),
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  }, []);

  const getSiteSalaryHeadDetail = useCallback(async () => {
    if (!location?.state?.id) return;
    try {
      const res: ISalaryHeadDetailResponse = await callApi(
        API_URL +
          API_ENDPOINT.getSiteSalaryHeadDetail +
          `?siteId=${location?.state?.id}`,
        { method: 'GET' },
      );

      if (res?.status) {
        const earning: ISalaryHeadConfig[] = [];
        const deduction: ISalaryHeadConfig[] = [];
        for (const item of res.data) {
          if (item?.salaryhead?.type === 'earning') {
            earning.push(item);
          } else {
            deduction.push(item);
          }
        }

        setSelectedSalaryHeads((prev) => ({
          ...prev,
          earning: earning,
          deduction: deduction,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  }, [location?.state?.id]);

  useEffect(() => {
    getOfficeList();
    getSalaryHead('earning');
    getSalaryHead('deduction');
    if (location?.state?.id) {
      getSiteSalaryHeadDetail();
      const timer = setTimeout(() => {
        setSelectedOfficeId(location?.state?.id);
        reset({
          officeId: location?.state?.id,
        });
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [
    getOfficeList,
    getSalaryHead,
    getSiteSalaryHeadDetail,
    location?.state?.id,
    reset,
  ]);

  const handleUpdateSalaryHead = (data: ISalaryHeadConfig, type: string) => {
    setModalData(data);
    if (type === 'earning') {
      toggleEarningModal();
    }
    if (type === 'deduction') {
      toggleDeductionModal();
    }
  };

  const handleRemoveSalaryHead = async () => {
    if (!selectedType) return;
    try {
      const response: IDeleteResponse = await callApi(
        API_URL +
          API_ENDPOINT.deleteSitesSalaryHead +
          `?siteSalaryHeadId=${selectedOfficeId}`,
        { method: 'GET' },
      );

      if (response?.status) {
        showToastNotification('success', response.message);
        setSelectedSalaryHeads((prev) => ({
          ...prev,
          [selectedType]: prev[selectedType]?.filter(
            (item) => item.id !== selectedId,
          ),
        }));
        toggleModal();
        getSiteSalaryHeadDetail();
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  };

  const ActionHandler = (data: string, type: TSalaryType) => {
    setSelectedType(type);
    setSelectedOfficeId(data);
    toggleModal();
  };

  const onSubmit = () => {};

  return (
    <Layout>
      <div className="content container-fluid">
        <BackButton label="Site Salary Head" />
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardLayout>
            <div className="tab-content" id="pills-tabContent">
              <div
                className="tab-pane fade show active"
                id="pills-BasicDetails"
                role="tabpanel"
                aria-labelledby="pills-BasicDetails-tab"
                tabIndex={0}
              >
                <div className="d-flex justify-content-between">
                  <div className="col-12 col-md-6 col-lg-6"></div>

                  <div className="d-flex gap-4 py-5">
                    <Button
                      className="btn btn-outline-success"
                      type="button"
                      onClick={() => {
                        if (!selectedOfficeId) {
                          showToastNotification(
                            'info',
                            'Please select office before add earning',
                          );
                        } else {
                          setModalData(null);
                          toggleEarningModal();
                        }
                      }}
                    >
                      Add Earning
                    </Button>
                    <Button
                      className="btn btn-outline-danger"
                      type="button"
                      onClick={() => {
                        if (!selectedOfficeId) {
                          showToastNotification(
                            'info',
                            'Please select office before add deduction',
                          );
                        } else {
                          setModalData(null);
                          toggleDeductionModal();
                        }
                      }}
                    >
                      Add Deduction
                    </Button>
                  </div>
                </div>
                <div className="row my-5">
                  <div className="col-12 col-md-6 mb-4">
                    <div className="border rounded p-3 h-100 bg-success-subtle">
                      <h4 className="text-center mb-3">Earnings</h4>

                      <div className="d-flex flex-column gap-2">
                        {selectedSalaryHeads?.earning?.length <= 0 ? (
                          <div className="text-muted text-center fst-italic">
                            No earnings selected
                          </div>
                        ) : (
                          selectedSalaryHeads?.earning?.map((item) => (
                            <div
                              className="border rounded px-3 py-2 d-flex justify-content-between align-items-center bg-light mx-2"
                              key={item?.id}
                            >
                              <span>{item?.salaryhead?.name}</span>
                              <div className="d-flex gap-2">
                                {!item?.salaryhead?.isBase && (
                                  <>
                                    <Button
                                      type="button"
                                      className="bg-transparent text-black"
                                      style={{
                                        border: 'none',
                                      }}
                                      onClick={() =>
                                        handleUpdateSalaryHead(item, 'earning')
                                      }
                                    >
                                      <EditIcon />
                                    </Button>
                                    <Button
                                      type="button"
                                      className="bg-transparent text-black"
                                      style={{
                                        border: 'none',
                                      }}
                                      onClick={() =>
                                        ActionHandler(
                                          item?.id,
                                          item?.salaryhead?.type as TSalaryType,
                                        )
                                      }
                                    >
                                      <DeleteUnfilledIcon />
                                    </Button>{' '}
                                  </>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-6 mb-4">
                    <div className="border rounded p-3 h-100 bg-danger-subtle">
                      <h4 className="text-center mb-3">Deductions</h4>

                      <div className="d-flex flex-column gap-2">
                        {selectedSalaryHeads?.deduction?.length <= 0 ? (
                          <div className="text-muted text-center fst-italic">
                            No deductions selected
                          </div>
                        ) : (
                          selectedSalaryHeads?.deduction?.map((item) => (
                            <div
                              className="border rounded px-3 py-2 d-flex justify-content-between align-items-center bg-light mx-2"
                              key={item?.id}
                            >
                              <span>{item?.salaryhead?.name}</span>
                              {!item?.salaryhead?.isBase && (
                                <div className="d-flex gap-2">
                                  <button
                                    type="button"
                                    className="bg-transparent text-black border-0"
                                    onClick={() =>
                                      handleUpdateSalaryHead(item, 'deduction')
                                    }
                                  >
                                    <EditIcon />
                                  </button>
                                  <button
                                    type="button"
                                    className="bg-transparent text-black border-0"
                                    onClick={() =>
                                      ActionHandler(
                                        item?.id,
                                        item?.salaryhead?.type as TSalaryType,
                                      )
                                    }
                                  >
                                    <DeleteUnfilledIcon />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {!location?.state?.id && (
                  <FormButtons isLoading={pageStates.isLoading}>
                    Add Site Salary head
                  </FormButtons>
                )}
              </div>
            </div>
          </CardLayout>
        </form>
      </div>
      <AddEarningModal
        modal={earningModal}
        toggle={toggleEarningModal}
        earningsList={pageStates?.earnings}
        selectedSalaryHeads={selectedSalaryHeads}
        selectedSiteId={selectedOfficeId}
        getSiteSalaryHeadDetail={getSiteSalaryHeadDetail}
        getSalaryHead={getSalaryHead}
        modalData={modalData}
        setModalData={setModalData}
      />
      <AddDeductionModal
        modal={deductionModal}
        toggle={toggleDeductionModal}
        deductionsList={pageStates?.deductions}
        selectedSalaryHeads={selectedSalaryHeads}
        selectedSiteId={selectedOfficeId}
        getSiteSalaryHeadDetail={getSiteSalaryHeadDetail}
        getSalaryHead={getSalaryHead}
        modalData={modalData}
        setModalData={setModalData}
      />
      <ConfirmModal
        isOpen={modal}
        toggle={toggleModal}
        submitHandler={handleRemoveSalaryHead}
      />
    </Layout>
  );
};

export default AddSiteSalaryHead;
